<template>
  <div class="file-upload-manager">
    <!-- Lớp phủ khi đang tải lên -->
    <div v-if="isSaving" class="upload-loading-overlay">
      <div class="upload-loading-content">
        <b-spinner
          variant="primary"
          style="width: 3rem; height: 3rem"
        ></b-spinner>
        <h5 class="mt-3 mb-2">Đang tải tệp lên...</h5>
        <p class="text-muted">
          Vui lòng chờ, đang xử lý {{ pendingFiles.length }} tệp
        </p>
      </div>
    </div>

    <!-- Xem ảnh: tệp chờ tải lên -->
    <vue-easy-lightbox
      escDisabled
      :visible="previewPending.visible"
      :imgs="previewPending.imgs"
      :index="previewPending.index"
      @hide="previewPending.visible = false"
    ></vue-easy-lightbox>

    <!-- Xem ảnh: tệp đã đính kèm -->
    <vue-easy-lightbox
      escDisabled
      :visible="previewAttachment.visible"
      :imgs="previewAttachment.imgs"
      :index="previewAttachment.index"
      @hide="previewAttachment.visible = false"
    ></vue-easy-lightbox>

    <!-- Xem tệp không phải ảnh (PDF...) -->
    <b-modal v-model="fileModal.visible" size="xl" hide-footer body-class="p-0">
      <template #modal-title>
        <div>
          <div>{{ fileModal.fileName }}</div>
          <small
            v-if="fileModal.subtitle"
            class="text-muted font-weight-normal"
          >
            {{ fileModal.subtitle }}
          </small>
        </div>
      </template>

      <div v-if="fileModal.loading" class="text-center p-5">
        <b-spinner variant="primary"></b-spinner>
        <p class="text-muted mt-3 mb-0">Đang tải nội dung tệp...</p>
      </div>
      <div v-else-if="fileModal.category === 'pdf'" style="height: 80vh">
        <iframe
          :src="fileModal.url"
          width="100%"
          height="100%"
          style="border: none"
        />
      </div>
      <div v-else class="text-center p-5">
        <i class="fas fa-file fa-4x text-secondary mb-3 d-block"></i>
        <p class="text-muted">Không xem trước được loại tệp này.</p>
        <a
          :href="fileModal.url"
          :download="fileModal.fileName"
          class="btn btn-primary btn-sm"
        >
          <i class="fas fa-download mr-1"></i> Tải về
        </a>
      </div>
    </b-modal>

    <!-- Guard nằm trong component để không màn nào quên -->
    <b-alert v-if="!entityId" show variant="info" class="mb-3">
      <i class="fas fa-info-circle mr-2"></i>
      <span v-html="emptyHint"></span>
    </b-alert>

    <template v-else>
      <!-- Tệp đã đính kèm -->
      <div
        v-if="attachments.length > 0"
        class="d-flex align-items-center justify-content-between mb-3"
      >
        <span class="font-weight-bold text-dark">
          <i class="fas fa-folder-open text-primary mr-2"></i>
          Tệp đã đính kèm
        </span>
        <b-badge :variant="isMaxFilesReached ? 'danger' : 'primary'" pill>
          {{ attachments.length }}/{{ LIMITS.MAX_ATTACHMENTS }} tệp
        </b-badge>
      </div>

      <b-col v-if="isLoadAttachmentSuccess" class="preview-img-container mb-4">
        <div v-if="attachments.length === 0" class="text-muted text-center">
          Chưa có tệp đính kèm nào.
        </div>
        <div v-else class="d-flex flex-wrap">
          <div
            v-for="item in attachments"
            :key="item.id"
            v-b-tooltip.hover.html.top="buildTooltip(item)"
            class="img-container align-items-start"
          >
            <img
              v-if="item.category === 'image' && item.url"
              :src="item.url"
              class="preview-img"
              width="80"
              height="100"
              :alt="item.originalName"
              @click="openAttachmentPreview(item)"
            />
            <div
              v-else
              class="file-type-icon"
              @click="openAttachmentPreview(item)"
            >
              <i :class="getFileTypeIcon(item.originalName)"></i>
            </div>

            <div class="file-name-label">
              {{ truncateFileName(item.originalName) }}
            </div>

            <i
              v-if="!disabled"
              class="fas fa-times-circle text-danger close"
              title="Xóa tệp"
              @click="deleteAttachment(item)"
            ></i>
            <i
              class="fas fa-arrow-circle-down text-warning remove"
              title="Tải về"
              @click="downloadAttachment(item)"
            ></i>
          </div>
        </div>
      </b-col>

      <div v-else class="preview-img-container mb-4 text-center text-muted">
        <b-spinner small class="mr-2"></b-spinner> Đang tải danh sách tệp...
      </div>

      <!-- Chọn và tải tệp -->
      <form
        enctype="multipart/form-data"
        novalidate
        class="mb-4"
        @submit.prevent
      >
        <b-button
          size="sm"
          variant="secondary"
          class="font-weight-bolder mr-2"
          :disabled="disabled || isSaving || isMaxFilesReached"
          @click="openFilePicker()"
        >
          <i class="fas fa-paperclip"></i>
          Đính kèm tệp
        </b-button>

        <b-button
          v-if="pendingFiles.length"
          size="sm"
          variant="warning"
          class="font-weight-bolder"
          :disabled="disabled || isSaving"
          @click="submitFileUpload()"
        >
          <span v-if="isSaving">
            <b-spinner small class="mr-1"></b-spinner>
            Đang tải lên...
          </span>
          <span v-else>
            <i class="fas fa-upload"></i>
            Tải tệp lên
          </span>
        </b-button>

        <input
          ref="fileInput"
          type="file"
          class="d-none"
          multiple
          :accept="LIMITS.ACCEPT_ATTR"
          :disabled="disabled || isSaving"
          @change="onFilesSelected($event)"
        />

        <div class="mt-2">
          <small class="text-muted">
            Chấp nhận {{ LIMITS.ALLOWED_LABEL }} · PDF tối đa
            {{ formatFileSizeMB(LIMITS.MAX_FILE_SIZE_PDF) }}, ảnh
            {{ formatFileSizeMB(LIMITS.MAX_FILE_SIZE_IMAGE) }}, tài liệu
            {{ formatFileSizeMB(LIMITS.MAX_FILE_SIZE_DOCUMENT) }} ·
            {{ LIMITS.MAX_ATTACHMENTS }} tệp mỗi bản ghi
          </small>
        </div>
      </form>

      <!-- Tệp chờ tải lên -->
      <div v-if="pendingFiles.length > 0">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <span class="font-weight-bold text-dark">
            <i class="fas fa-cloud-upload-alt text-warning mr-2"></i>
            Tệp chờ tải lên
          </span>
          <b-badge
            :variant="
              pendingFiles.length >= LIMITS.MAX_PENDING_FILES
                ? 'danger'
                : 'warning'
            "
            pill
          >
            {{ pendingFiles.length }}/{{ LIMITS.MAX_PENDING_FILES }} tệp
          </b-badge>
        </div>

        <b-col class="preview-img-container mb-4">
          <div class="d-flex flex-wrap">
            <div
              v-for="(item, index) in pendingFiles"
              :key="item.key"
              v-b-tooltip.hover.top="
                `${item.originalName} (${formatFileSizeMB(item.size)})`
              "
              class="img-container align-items-start"
            >
              <img
                v-if="item.category === 'image'"
                :src="item.url"
                class="preview-img"
                width="80"
                height="100"
                :alt="item.originalName"
                @click="openPendingPreview(item)"
              />
              <div
                v-else
                class="file-type-icon"
                @click="openPendingPreview(item)"
              >
                <i :class="getFileTypeIcon(item.originalName)"></i>
              </div>

              <div class="file-name-label">
                {{ truncateFileName(item.originalName) }}
              </div>

              <i
                class="fas fa-times-circle text-danger close"
                title="Bỏ tệp này"
                @click="removePendingFile(index)"
              ></i>
            </div>
          </div>
        </b-col>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import VueEasyLightbox from 'vue-easy-lightbox';

import ApiService from '@/core/services/api.service';
import {
  makeToastSuccess,
  makeToastFaile,
  handleError,
  formatDate,
} from '@/utils/common';
import {
  getApiUrlByEntity,
  getFileCategory,
  getFileExtension,
  getFileTypeIcon,
  getMaxFileSizeByExtension,
  truncateFileName,
  formatFileSizeMB,
} from '@/utils/common-upload-files';
import { FILE_UPLOAD_LIMITS } from '@/utils/attachment-policy';
import { FILE_UPLOAD_MODE } from '@/utils/enum';
import { resizeImages } from '@/utils/resizeImage';

/**
 * Quản lý tệp đính kèm dùng chung cho hợp đồng mua bán và hóa đơn.
 *
 * Hai điểm khác bản gốc D-Core:
 * - Nạp lười: chỉ ảnh mới tải blob làm thumbnail, PDF đợi người dùng bấm xem. Bản cũ
 *   kéo về toàn bộ nội dung khi mở màn — 10 tệp PDF 30MB là đứng vài chục giây.
 * - Có `accept` và kiểm hạn mức phía client, thay vì chờ hết lượt upload mới báo lỗi.
 */
export default {
  name: 'FileUploadManager',
  components: { VueEasyLightbox },
  props: {
    /** `contractInfo.id` với hợp đồng, `billNumber` với hóa đơn. */
    entityId: {
      type: [String, Number],
      default: null,
    },
    /** Khớp FileFolderEnum của backend: bills | contracts | test-drives. */
    entity: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /** Lời nhắc khi bản ghi chưa lưu — mỗi nghiệp vụ gọi tên khác nhau. */
    emptyHint: {
      type: String,
      default: 'Vui lòng <strong>lưu bản ghi</strong> trước khi đính kèm tệp',
    },
  },
  data() {
    return {
      LIMITS: FILE_UPLOAD_LIMITS,
      attachments: [],
      isLoadAttachmentSuccess: false,
      pendingFiles: [],
      currentStatus: FILE_UPLOAD_MODE.INITIAL,
      pendingKeyCounter: 0,
      previewPending: { visible: false, imgs: [], index: 0 },
      previewAttachment: { visible: false, imgs: [], index: 0 },
      fileModal: {
        visible: false,
        loading: false,
        url: '',
        fileName: '',
        subtitle: '',
        category: '',
      },
      /** Thu hồi ở beforeDestroy. */
      objectUrls: [],
    };
  },
  computed: {
    isSaving() {
      return this.currentStatus === FILE_UPLOAD_MODE.SAVING;
    },
    totalFileCount() {
      return this.attachments.length + this.pendingFiles.length;
    },
    isMaxFilesReached() {
      return this.totalFileCount >= this.LIMITS.MAX_ATTACHMENTS;
    },
    apiUrl() {
      return getApiUrlByEntity(this.entity);
    },
  },
  watch: {
    // Modal ở màn danh sách giữ nguyên component và chỉ đổi entityId, nên phải dọn
    // state cũ, nếu không tệp của bản ghi trước vẫn còn hiển thị.
    entityId: {
      immediate: true,
      handler(newVal) {
        this.resetPending();
        this.attachments = [];
        this.isLoadAttachmentSuccess = false;
        if (newVal) {
          this.loadAttachments();
        }
      },
    },
  },
  beforeDestroy() {
    this.revokeAllObjectUrls();
  },
  methods: {
    getFileTypeIcon,
    truncateFileName,
    formatFileSizeMB,

    /** Tooltip nhiều dòng nên phải trả HTML — dùng kèm modifier `.html`. */
    buildTooltip(item) {
      const lines = [item.originalName];
      if (item.uploadedByName)
        lines.push(`Tải lên bởi: ${item.uploadedByName}`);
      if (item.createdAt) lines.push(`Lúc: ${formatDate(item.createdAt)}`);
      return lines.join('<br>');
    },

    trackObjectUrl(url) {
      this.objectUrls.push(url);
      return url;
    },

    revokeAllObjectUrls() {
      this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
      this.objectUrls = [];
    },

    /* ------------------------------------------------------------------ */
    /* Nạp danh sách                                                       */
    /* ------------------------------------------------------------------ */

    async loadAttachments() {
      if (!this.entityId) return;

      const requestedEntityId = this.entityId;
      this.isLoadAttachmentSuccess = false;

      try {
        const resp = await ApiService.query(this.apiUrl.root, {
          params: { entity: this.entity, entityId: this.entityId },
        });

        // Có thể đã chuyển sang bản ghi khác trong lúc chờ.
        if (requestedEntityId !== this.entityId) return;

        if (resp.data.status !== 1) {
          this.isLoadAttachmentSuccess = false;
          return;
        }

        this.attachments = (resp.data.data || []).map((item) => ({
          id: item.id,
          fileName: item.fileName,
          originalName: item.name || item.fileName,
          category: getFileCategory(item.fileName),
          uploadedByName: item.uploadedByName || null,
          createdAt: item.createdAt || null,
          url: null,
        }));
        this.isLoadAttachmentSuccess = true;

        // Chỉ ảnh cần nội dung ngay để làm thumbnail.
        await Promise.all(
          this.attachments
            .filter((item) => item.category === 'image')
            .map((item) => this.ensureFileContent(item)),
        );
      } catch (error) {
        if (requestedEntityId !== this.entityId) return;
        this.isLoadAttachmentSuccess = false;
        handleError(error);
      }
    },

    /** Tải nội dung về objectURL, chỉ một lần mỗi tệp. `null` nếu thất bại. */
    async ensureFileContent(item) {
      if (item.url) return item.url;

      try {
        const response = await axios.get(this.apiUrl.preview, {
          params: {
            fileName: item.fileName,
            entity: this.entity,
            entityId: this.entityId,
          },
          headers: { 'device-type': '2' },
          responseType: 'blob',
        });

        const blob = new Blob([response.data], {
          type: response.headers['content-type'],
        });
        const url = this.trackObjectUrl(URL.createObjectURL(blob));

        // $set vì `url` khởi tạo là null trong mảng đã reactive.
        this.$set(item, 'url', url);
        return url;
      } catch (error) {
        console.error('Không tải được nội dung tệp:', item.fileName, error);
        return null;
      }
    },

    /* ------------------------------------------------------------------ */
    /* Xem trước                                                           */
    /* ------------------------------------------------------------------ */

    openAttachmentPreview(item) {
      if (item.category === 'image') {
        const images = this.attachments.filter(
          (file) => file.category === 'image' && file.url,
        );
        const index = images.findIndex((file) => file.id === item.id);
        if (index === -1) {
          makeToastFaile('Không tải được nội dung ảnh');
          return;
        }
        this.previewAttachment.imgs = images.map((file) => ({
          src: file.url,
          title: file.originalName,
        }));
        this.previewAttachment.index = index;
        this.previewAttachment.visible = true;
        return;
      }

      this.openFileModal(item, () => this.ensureFileContent(item));
    },

    openPendingPreview(item) {
      if (item.category === 'image') {
        const images = this.pendingFiles.filter(
          (file) => file.category === 'image',
        );
        const index = images.findIndex((file) => file.key === item.key);
        this.previewPending.imgs = images.map((file) => ({
          src: file.url,
          title: file.originalName,
        }));
        this.previewPending.index = index;
        this.previewPending.visible = true;
        return;
      }

      this.openFileModal(item, () => Promise.resolve(item.url));
    },

    buildSubtitle(item) {
      if (!item.uploadedByName) return '';
      const at = item.createdAt ? ` · ${formatDate(item.createdAt)}` : '';
      return `Tải lên bởi: ${item.uploadedByName}${at}`;
    },

    async openFileModal(item, resolveUrl) {
      this.fileModal = {
        visible: true,
        loading: true,
        url: '',
        fileName: item.originalName,
        subtitle: this.buildSubtitle(item),
        category: item.category,
      };

      const url = await resolveUrl();

      if (!url) {
        this.fileModal.visible = false;
        makeToastFaile(`Không tải được nội dung tệp ${item.originalName}`);
        return;
      }

      this.fileModal.url = url;
      this.fileModal.loading = false;
    },

    async downloadAttachment(item) {
      const url = await this.ensureFileContent(item);
      if (!url) {
        makeToastFaile(`Không tải được tệp ${item.originalName}`);
        return;
      }

      const link = document.createElement('a');
      link.href = url;
      link.download = item.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    /* ------------------------------------------------------------------ */
    /* Chọn tệp                                                            */
    /* ------------------------------------------------------------------ */

    openFilePicker() {
      this.$refs.fileInput.click();
    },

    async onFilesSelected(event) {
      const fileList = Array.from(event.target.files || []);
      // Dọn input để chọn lại đúng tệp vừa bỏ ra vẫn kích hoạt change.
      event.target.value = '';

      if (!fileList.length) return;

      const invalidType = fileList.filter(
        (file) =>
          !this.LIMITS.ALLOWED_EXTENSIONS.includes(getFileExtension(file.name)),
      );
      if (invalidType.length) {
        makeToastFaile(
          `Không hỗ trợ định dạng của: ${invalidType
            .map((file) => file.name)
            .join(', ')}. Chỉ nhận ${this.LIMITS.ALLOWED_LABEL}`,
        );
        return;
      }

      if (
        this.pendingFiles.length + fileList.length >
        this.LIMITS.MAX_PENDING_FILES
      ) {
        makeToastFaile(
          `Mỗi lượt chỉ tải lên tối đa ${this.LIMITS.MAX_PENDING_FILES} tệp`,
        );
        return;
      }

      if (this.totalFileCount + fileList.length > this.LIMITS.MAX_ATTACHMENTS) {
        makeToastFaile(
          `Mỗi bản ghi chỉ đính kèm tối đa ${this.LIMITS.MAX_ATTACHMENTS} tệp. ` +
            `Hiện đã có ${this.totalFileCount} tệp, vui lòng xóa bớt trước khi thêm.`,
        );
        return;
      }

      this.currentStatus = FILE_UPLOAD_MODE.SAVING;

      try {
        // Nén trước rồi mới kiểm dung lượng: ảnh 40MB thường co xuống dưới 2MB,
        // chặn theo kích thước gốc sẽ từ chối oan.
        const processed = await resizeImages(fileList);

        // Nêu trần của CHÍNH tệp bị chặn — nói con số chung sẽ khiến người dùng tưởng
        // tệp Word 8MB của mình hợp lệ.
        const oversized = processed
          .map((file, index) => ({ file, name: fileList[index].name }))
          .filter(
            ({ file, name }) => file.size > getMaxFileSizeByExtension(name),
          );

        if (oversized.length) {
          makeToastFaile(
            `Vượt dung lượng cho phép: ${oversized
              .map(
                ({ file, name }) =>
                  `${name} (${formatFileSizeMB(file.size)}, tối đa ` +
                  `${formatFileSizeMB(getMaxFileSizeByExtension(name))})`,
              )
              .join(', ')}`,
          );
          return;
        }

        const pendingSize = this.pendingFiles.reduce(
          (sum, item) => sum + item.size,
          0,
        );
        const totalSize =
          pendingSize + processed.reduce((sum, file) => sum + file.size, 0);
        if (totalSize > this.LIMITS.MAX_TOTAL_SIZE) {
          makeToastFaile(
            `Tổng dung lượng một lượt tải lên tối đa ${formatFileSizeMB(
              this.LIMITS.MAX_TOTAL_SIZE,
            )}, hiện đang là ${formatFileSizeMB(totalSize)}`,
          );
          return;
        }

        this.pendingFiles = this.pendingFiles.concat(
          processed.map((file, index) => ({
            key: ++this.pendingKeyCounter,
            file,
            // resizeImage đổi ảnh sang JPEG nhưng ta giữ tên gốc để hiển thị.
            originalName: fileList[index].name,
            size: file.size,
            category: getFileCategory(fileList[index].name),
            url: this.trackObjectUrl(URL.createObjectURL(file)),
          })),
        );
      } catch (error) {
        handleError(error);
      } finally {
        this.currentStatus = FILE_UPLOAD_MODE.INITIAL;
      }
    },

    removePendingFile(index) {
      const [removed] = this.pendingFiles.splice(index, 1);
      if (removed) {
        URL.revokeObjectURL(removed.url);
        this.objectUrls = this.objectUrls.filter((url) => url !== removed.url);
      }
    },

    resetPending() {
      this.pendingFiles.forEach((item) => URL.revokeObjectURL(item.url));
      this.pendingFiles = [];
      this.currentStatus = FILE_UPLOAD_MODE.INITIAL;
    },

    /* ------------------------------------------------------------------ */
    /* Tải lên và xóa                                                      */
    /* ------------------------------------------------------------------ */

    async submitFileUpload() {
      if (!this.entityId) {
        makeToastFaile('Vui lòng lưu bản ghi trước khi tải tệp lên');
        return;
      }
      if (!this.pendingFiles.length) return;

      const formData = new FormData();
      this.pendingFiles.forEach((item) => {
        formData.append('files', item.file, item.originalName);
      });
      formData.append('entity', this.entity);
      formData.append('entityId', this.entityId);

      this.currentStatus = FILE_UPLOAD_MODE.SAVING;
      const uploadedCount = this.pendingFiles.length;

      try {
        const resp = await axios.post(this.apiUrl.upload, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'device-type': '2',
          },
        });

        if (resp.data.status !== 1) {
          makeToastFaile(resp.data.message || 'Tải tệp lên thất bại!');
          return;
        }

        makeToastSuccess(`Tải lên thành công ${uploadedCount} tệp`);
        this.resetPending();
        await this.loadAttachments();
        this.$emit('uploaded');
      } catch (error) {
        // handleError bóc message thật từ server thay vì một dòng chung chung.
        handleError(error);
      } finally {
        this.currentStatus = FILE_UPLOAD_MODE.INITIAL;
      }
    },

    async deleteAttachment(item) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Xóa tệp "${item.originalName}"? Thao tác này không hoàn tác được.`,
        {
          title: 'Xác nhận xóa tệp',
          okTitle: 'Xóa',
          cancelTitle: 'Hủy',
          okVariant: 'danger',
          centered: true,
        },
      );
      if (!confirmed) return;

      // ApiService.delete tự toast rồi RESOLVE với chính object lỗi — không có nhánh
      // reject để bắt, và không được toast lần nữa.
      const resp = await ApiService.delete(`${this.apiUrl.root}/${item.id}`);

      if (!resp || !resp.data || resp.data.status !== 1) return;

      makeToastSuccess('Xóa tệp thành công!');
      await this.loadAttachments();
      this.$emit('deleted');
    },
  },
};
</script>

<style lang="scss" scoped>
.file-upload-manager {
  position: relative;

  .upload-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    z-index: 1000;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .upload-loading-content {
    text-align: center;
  }

  .img-container {
    position: relative;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    width: 80px;
    margin: 0px 10px;
  }

  .preview-img {
    max-width: 80px;
    padding: 10px;
    object-fit: contain;
    border: 1px solid #eeecff;
    border-radius: 5px;
    margin-bottom: 4px;
    cursor: pointer;
  }

  .file-type-icon {
    width: 80px;
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #eeecff;
    border-radius: 5px;
    margin-bottom: 4px;
    cursor: pointer;
    background: #f9f9ff;
  }

  .file-name-label {
    font-size: 10px;
    color: #555;
    text-align: center;
    word-break: break-all;
    max-width: 80px;
    line-height: 1.2;
    margin-bottom: 16px;
  }

  .preview-img-container {
    border: 2px dashed #eeecff;
    padding: 2rem 1rem;
  }

  .close {
    position: absolute;
    top: 0px;
    right: 0px;
    width: 15px;
    cursor: pointer;
  }

  .remove {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 15px;
    cursor: pointer;
  }
}
</style>
